'use client';

import { useState, useEffect } from 'react';
import Image from 'next/image';

interface GalleryImage {
    url: string;
    alt?: { en?: string; ar?: string };
}

interface ProjectGalleryProps {
    images: GalleryImage[];
    title: string;
    locale: string;
    countLabel: string;
}

export function ProjectGallery({ images, title, locale, countLabel }: ProjectGalleryProps) {
    const [index, setIndex] = useState<number | null>(null);

    const isOpen = index !== null;
    const lang   = locale === 'ar' ? 'ar' : 'en';

    const open  = (i: number) => setIndex(i);
    const close = () => setIndex(null);
    const prev  = () => setIndex(i => i === null ? null : (i === 0 ? images.length - 1 : i - 1));
    const next  = () => setIndex(i => i === null ? null : (i === images.length - 1 ? 0 : i + 1));

    useEffect(() => {
        if (!isOpen) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape')     close();
            if (e.key === 'ArrowLeft')  prev();
            if (e.key === 'ArrowRight') next();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, images.length]);

    useEffect(() => {
        document.body.style.overflow = isOpen ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [isOpen]);

    const altFor = (img: GalleryImage, i: number) =>
        img.alt?.[lang] || `${title} — ${i + 1}`;

    return (
        <>
            {/* ── Gallery grid ──────────────────────────────────────────────── */}
            <div className="space-y-4">
                {/* First image — full-width */}
                <button
                    type="button"
                    onClick={() => open(0)}
                    className="relative w-full h-64 sm:h-80 rounded-2xl overflow-hidden border border-(--border) group cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                    aria-label={`Open image 1 of ${images.length}`}
                >
                    <Image
                        src={images[0].url}
                        alt={altFor(images[0], 0)}
                        fill
                        className="object-cover group-hover:scale-[1.03] transition-transform duration-700 ease-out"
                        sizes="(max-width:1024px) 100vw, 66vw"
                    />
                    <div className="absolute inset-0 bg-linear-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                    <span className="absolute bottom-3 inset-e-3 bg-black/50 text-white text-xs rounded-lg px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                        🔍
                    </span>
                </button>

                {/* Remaining images — 2-col grid */}
                {images.length > 1 && (
                    <div className="grid grid-cols-2 gap-4">
                        {images.slice(1).map((img, idx) => (
                            <button
                                key={idx + 1}
                                type="button"
                                onClick={() => open(idx + 1)}
                                className="relative rounded-xl overflow-hidden border border-(--border) group cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                                style={{ height: '200px' }}
                                aria-label={`Open image ${idx + 2} of ${images.length}`}
                            >
                                <Image
                                    src={img.url}
                                    alt={altFor(img, idx + 1)}
                                    fill
                                    className="object-cover group-hover:scale-[1.05] transition-transform duration-500 ease-out"
                                    sizes="(max-width:640px) 50vw, (max-width:1024px) 33vw, 22vw"
                                />
                                <div className="absolute inset-0 ring-1 ring-inset ring-white/0 group-hover:ring-primary-500/30 rounded-xl transition-all duration-300 pointer-events-none" />
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* ── Lightbox ──────────────────────────────────────────────────── */}
            {isOpen && index !== null && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/95"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Image viewer"
                    onClick={close}
                >
                    {/* Close */}
                    <button
                        type="button"
                        onClick={close}
                        className="absolute top-4 inset-e-4 z-10 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors cursor-pointer"
                        aria-label="Close"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>

                    {/* Prev */}
                    {images.length > 1 && (
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); prev(); }}
                            className="absolute inset-s-4 z-10 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors cursor-pointer"
                            aria-label="Previous image"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                            </svg>
                        </button>
                    )}

                    {/* Image */}
                    <div
                        className="relative max-w-5xl max-h-[85vh] w-full mx-16 rounded-2xl overflow-hidden shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Image
                            src={images[index].url}
                            alt={altFor(images[index], index)}
                            width={1280}
                            height={800}
                            className="object-contain w-full h-auto max-h-[85vh]"
                            sizes="(max-width: 768px) 100vw, 80vw"
                        />
                    </div>

                    {/* Next */}
                    {images.length > 1 && (
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); next(); }}
                            className="absolute inset-e-4 z-10 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors cursor-pointer"
                            aria-label="Next image"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                            </svg>
                        </button>
                    )}

                    {/* Counter */}
                    {images.length > 1 && (
                        <p className="absolute bottom-5 left-1/2 -translate-x-1/2 text-white/60 text-sm tabular-nums pointer-events-none">
                            {index + 1} / {images.length} {countLabel}
                        </p>
                    )}
                </div>
            )}
        </>
    );
}
